import type { MotionRecord } from '@cartwhl/common/api/getMotionsByGroup';

import {
  parseMotionExportURLSlots,
  previewBvhUrlsFromExportSlots,
} from './batchMotionExportUrls';
import { isMhrCharacterId } from './freeCharacterMesh';

export type TimelineMotionRig = 'axel' | 'mhr';

export function resolveTimelineMotionRigFromCharacterId(
  characterID: string,
): TimelineMotionRig {
  return isMhrCharacterId(characterID) ? 'mhr' : 'axel';
}

export function isKimodoTextToMotionCompatibleWithTimelineRig(
  requiredRig: TimelineMotionRig,
): boolean {
  // MHR Kimodo text-to-motion is temporarily disabled until the bridge is fixed.
  return requiredRig === 'axel';
}

export function isMotionLibraryCompatibleWithTimelineRig(
  requiredRig: TimelineMotionRig,
): boolean {
  return requiredRig === 'axel';
}

export function isSwingBatchCompatibleWithTimelineRig(
  requiredRig: TimelineMotionRig | null | undefined,
): boolean {
  return requiredRig !== 'mhr';
}

const TIMELINE_MOTION_PICKER_BASE_TYPE_FILTERS = [
  'canvas-project',
  'comic-batch',
] as const;

export function getTimelineMotionPickerTypeFilters(
  requiredRig: TimelineMotionRig | null | undefined,
): Array<
  | (typeof TIMELINE_MOTION_PICKER_BASE_TYPE_FILTERS)[number]
  | 'swing-batch'
> {
  if (isSwingBatchCompatibleWithTimelineRig(requiredRig)) {
    return [...TIMELINE_MOTION_PICKER_BASE_TYPE_FILTERS, 'swing-batch'];
  }

  return [...TIMELINE_MOTION_PICKER_BASE_TYPE_FILTERS];
}

function hasAlignedMhrSidecars(motion: MotionRecord): boolean {
  const mhrBvhs =
    motion.mhrBvhURLs?.map((url) => url.trim()).filter(Boolean) ?? [];
  const mhrIds =
    motion.mhrIdentityJsonURLs?.map((url) => url.trim()).filter(Boolean) ?? [];
  return (
    mhrBvhs.length > 0 &&
    mhrIds.length > 0 &&
    mhrBvhs.length === mhrIds.length
  );
}

function hasAxelStudioBvh(motion: MotionRecord): boolean {
  if (motion.bvhURL?.trim()) {
    return true;
  }

  const bvhURLs = motion.bvhURLs ?? motion.bvh_urls ?? [];
  if (bvhURLs.some((url) => url.trim())) {
    return true;
  }

  if (motion.outputs?.some((output) => output?.bvh?.trim())) {
    return true;
  }

  const exportURLs = parseMotionExportURLSlots(
    motion.exportURLs ?? motion.export_urls,
  );
  return previewBvhUrlsFromExportSlots(exportURLs).length > 0;
}

export function motionRecordHasCompatibleTimelineRig(
  motion: MotionRecord,
  requiredRig: TimelineMotionRig,
): boolean {
  if (requiredRig === 'mhr') {
    return hasAlignedMhrSidecars(motion);
  }

  return hasAxelStudioBvh(motion);
}

export function filterMotionRecordsForTimelineRig<
  T extends MotionRecord,
>(motions: readonly T[], requiredRig: TimelineMotionRig | null | undefined) {
  if (!requiredRig) {
    return [...motions];
  }

  return motions.filter((motion) =>
    motionRecordHasCompatibleTimelineRig(motion, requiredRig),
  );
}
